import uniqueId from 'lodash/uniqueId';
import Link from 'next/link';
import React, { useState } from 'react';

import AwardsTable from 'components/AwardsTable/AwardsTable';
import Badge from 'components/Badge';
import Headline from 'components/Headline/Headline';
import Overlay from 'components/Overlay/Overlay';
import RichText from 'components/RichText/RichText';
import useOverElement from 'hooks/useOverElement';
import useCopyStore from 'store/copy';
import { setCursorLabel, triggerMoveMeCursor } from 'utils/cursorController';
import { handleMouseLeave } from 'utils/cursorGlobal';
import { ROUTES } from 'utils/routes';
import {
  Award,
  AwardBlock,
  MediaCMS,
} from 'utils/sharedStrapiQueries/sharedTypes';

import * as S from '../AboutPage.styles';

export interface AwardsSectionProps {
  heading: string;
  projectLabel: string;
  organisationLabel: string;
  awardLabel: string;
  dropdownLabel: string;
  image: MediaCMS;
  awardItem: Array<AwardBlock>;
  button: string;
  sectionTitle: string;
  awards: Array<Award>;
}

const AwardsSection: React.FunctionComponent<AwardsSectionProps> = props => {
  const { globalSettings } = useCopyStore(({ copy }) => copy);
  const [showOverlay, setShowOverlay] = useState<boolean>(false);

  const [ref] = useOverElement();

  const handleMouseEnter = () => {
    setCursorLabel('Move me');
    triggerMoveMeCursor(true);
  };

  const awards = props.awardItem.map(item => {
    const awardList = (
      <S.AwardsGridListItem key={uniqueId()}>
        <S.AwardIcon src={item.icon.url} alt={item.icon.alternativeText} />
        <S.AwardMiddleContent>
          <RichText
            as="h2"
            size="heading2"
            richText={item?.middleTopContent || ''}
            defaultFormatting
          />
          <RichText
            as="span"
            size="smallBody"
            richText={item?.middleBottomContent || ''}
            defaultFormatting
          />
        </S.AwardMiddleContent>

        <S.Awards richText={item.awardCategories} />
      </S.AwardsGridListItem>
    );

    if (item.relatedArticle?.slug) {
      return (
        <S.Link>
          <Link
            href={{
              pathname: ROUTES.AWARD,
              query: { slug: item.relatedArticle?.slug },
            }}
          >
            {awardList}
          </Link>
        </S.Link>
      );
    }
    return awardList;
  });

  return (
    <S.AwardsSectionWrapper ref={ref}>
      <S.AboutSectionHeadline>
        <Headline text={props?.sectionTitle} size="heading2" />
        <RichText
          as="p"
          size="smallBody"
          richText={props.heading}
          defaultFormatting
        />
      </S.AboutSectionHeadline>

      <S.Slider>
        <S.LeadershipSliderContent
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {awards}
        </S.LeadershipSliderContent>
      </S.Slider>

      {props.button && (
        <S.AwardsButtonWrapper>
          <Badge
            as="button"
            onClick={() => setShowOverlay(true)}
            hoverTextColor={
              globalSettings?.buttonsHoverColor ||
              globalSettings?.buttonsHoverDefaultColors
            }
          >
            {props.button}
          </Badge>
        </S.AwardsButtonWrapper>
      )}

      <Overlay
        isOpen={showOverlay}
        onExitClick={() => setShowOverlay(false)}
        withSpacing
        animation="fade"
        delay={0}
      >
        <AwardsTable {...props} />
      </Overlay>
    </S.AwardsSectionWrapper>
  );
};
export default AwardsSection;
